import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Formik } from "formik";
import { DefaultLayout } from "../../layouts/Default";
import { TopNavigation } from "../../themes/Default/components/TopNavigation";
import { Breadcrumps } from "../../themes/Default/components/Breadcrumps";
import { Link } from "../../themes/Default/components/Breadcrumps/components/Link";
import { Main } from "../../themes/Default/components/Main";
import { Tabs } from "../../themes/Default/components/Tabs";
import { Button, ButtonBack } from "../../themes/Default/components/TopNavigation/components/Button";
import { Form } from "../../themes/Default/components/Form";
import { Wizard } from "../../themes/Default/components/Wizard";
import { Step } from "../../themes/Default/components/Wizard/Step";
import { Row } from "../../themes/Default/components/Form/components/Row";
import { Field } from "../../themes/Default/components/Form/components/Row/components/Field";
import { Input } from "../../themes/Default/components/Form/components/Input";
import { Select } from "../../themes/Default/components/Form/components/Select";
import { Textarea } from "../../themes/Default/components/Form/components/Textarea";
import { prepareSelect } from "../../services/core/Select";
import { locationsManagement } from "../../hooks/api/LocationsManagement";
import { languagesManagement } from "../../hooks/api/LanguagesManagement";
import { languageProgramsManagement } from "../../hooks/api/LanguageProgramsManagement";
import { groupsManagement } from "../../hooks/api/GroupsManagement";
import { groupTypesManagement } from "../../hooks/api/GroupTypesManagement/GroupTypesManagement";
import { Title } from "../../themes/Default/components/Title";
import Divider from "../../themes/Default/components/Divider/Divider";
import { Toaster } from "../../themes/Default/components/Toaster";
import TimePicker from "../../themes/Default/components/Form/components/TimePicker/TimePicker";
import { format } from "date-fns";
import { calendarHolidaysManagement } from "../../hooks/api/CalendarHolidaysManagement";
import { settingsCallManagement } from "../../hooks/api/SettingsCallManagement";

const EditGroup = () => {
    const navigate = useNavigate();
    const { t } = useTranslation("common");
    const formRef = useRef(null);
    const { groupId } = useParams();
    const [isStarted, setIsStarted] = useState([]);
    const [groupName, setGroupName] = useState("");
    const [hasTests, setHasTests] = useState(false);
    const [hasProgress, setHasProgress] = useState(false);
    const [hasReport, setHasReport] = useState(false);
    const [hasPlan, setHasPlan] = useState(false);
    const [isArchived, setIsArchived] = useState("");
    const [locations, setLocations] = useState([]);
    const [startGroup, setStartGroup] = useState(true);
    const [canArchiveGroup, setCanArchiveGroup] = useState(true);
    const [languages, setLanguages] = useState([]);
    const [groupTypes, setGroupTypes] = useState([]);
    const [formErrors, setFormErrors] = useState([]);
    const [languageProgram, setLanguageProgram] = useState([]);
    const [isBlockClass, setIsBlockClass] = useState([]);
    const [classDuration, setClassDuration] = useState([]);

    const [appointment, setAppointment] = useState([]);
    const { useAppointmentListMutation } = settingsCallManagement;
    const [appointmentList] = useAppointmentListMutation();

    const [selectedWeekdays, setSelectedWeekdays] = useState([]);
    const [selectedWeekdays2, setSelectedWeekdays2] = useState([]);
    const weekDays = ["P", "U", "S", "Č", "P", "S", "N"];
    const toggleWeekday = (dayIndex) => {
        setSelectedWeekdays((prev) => (prev.includes(dayIndex) ? prev.filter((d) => d !== dayIndex) : [...prev, dayIndex]));
    };
    const toggleWeekday2 = (dayIndex) => {
        setSelectedWeekdays2((prev) => (prev.includes(dayIndex) ? prev.filter((d) => d !== dayIndex) : [...prev, dayIndex]));
    };

    const tabs = [
        ...(isStarted ? [
            { title: t('groups.attendance'), onClick: () => gotoAttendanceTab(groupId)  },
            ...(hasPlan ? [
                { title: t('groups.planning'), onClick: () => gotoPlanningTab(groupId)},
            ] : []),
            ...(hasProgress ? [
                { title: t('groups.progress'), onClick: () => gotoProgressTab(groupId)},
            ] : []), 
            ...(hasTests ? [
                { title: t('groups.progressTest'), onClick: () => gotoProgressTestTab(groupId) }
            ] : []), 
            ...(hasReport ? [
                { title: t('reports.title'),onClick: () => gotoReportTab(groupId)},
            ] : []),
            { title: t('documents'), onClick: () => gotoDocumentsTab(groupId)},
            { title: t('notes'), onClick: () => gotoNotesTab(groupId)},
        ] : []),
        { title: t("settings.title"), link: "/settings/reports", isActive: true },
    ];

    const gotoPlanningTab = (groupId) => {
        navigate("/groups/" + groupId + "/planning");
    }

    const gotoReportTab = (groupId) => {
        navigate("/groups/" + groupId + "/reports");
    }

    const gotoProgressTestTab = (groupId) => {
        navigate("/groups/" + groupId + "/tests");
    }

    const gotoProgressTab = (groupId) => {
        navigate("/groups/" + groupId + "/progress");
    }

    const gotoNotesTab = (groupId) => {
        navigate("/groups/" + groupId + "/notes");
    }

    const gotoDocumentsTab = (groupId) => {
        navigate("/groups/" + groupId + "/documents");
    }

    const gotoAttendanceTab = (groupId) => {
        navigate("/groups/" + groupId + "/attendance");
    }

    const { useLocationListMutation } = locationsManagement;
    const { useLanguageListMutation } = languagesManagement;
    const { useProgramListMutation } = languageProgramsManagement;
    const { 
        useUpdateGroupMutation, 
        useViewGroupMutation, 
        useCanGroupStartMutation, 
        useStartGroupMutation,
        useAddGroupDatesMutation,
        useViewProfessorMutation,
        useCanGroupArchiveMutation 
    } = groupsManagement;

    const { useGroupTypesListMutation } = groupTypesManagement;
    
    const [canGroupStart] = useCanGroupStartMutation();
    const [groupStart] = useStartGroupMutation();

    const [canGroupArchive] = useCanGroupArchiveMutation();

    const [locationList, { isLoading }] = useLocationListMutation();
    const [languageList] = useLanguageListMutation();
    const [languageProgramList] = useProgramListMutation();
    const [updateGroup] = useUpdateGroupMutation();
    const [viewGroup] = useViewGroupMutation();
    const [groupTypesList] = useGroupTypesListMutation();

    const { useCalendarHolidaysListMutation } = calendarHolidaysManagement;
    const [holidayList] = useCalendarHolidaysListMutation();
    const [addGroupDates] = useAddGroupDatesMutation();
    const [viewGroupProfessor] = useViewProfessorMutation();

    const [startDate, setStartDate] = useState([]);
    const [classNumber, setClassNumber] = useState();
    const [classStartTime, setClassStartTime] = useState([]);
    const [classStartTime2, setClassStartTime2] = useState([]);
    const [classEndTime ,setClassEndTime] = useState([]);

    const onSave = () => {
        if (formRef.current) {
            formRef.current.handleSubmit();
        }
    };

    const onSaved = (values) => {
        const body = {
            ...values,
            weekDays: selectedWeekdays,
            weekDays2: selectedWeekdays2,
        };        
        updateGroup({ body, groupId }).then((result) => {
            if (result?.error) {
                setFormErrors(result?.error?.data?.error?.violations);
                toast.error(result.error.data.error.responseMessage);
            } else {
                navigate("/groups/" + groupId + "/students/add");
            }
        });
    };

    const generateSelectedDates = async (totalClasses) => {
        if (!startDate || totalClasses <= 0) return [];
    
        const result = await holidayList();
        const holidays = result?.data?.records || [];
        const holidayDates = new Set(holidays.map(h => h.date));
    
        const classes = [];
        let currentDate = new Date(startDate);
    
        const weekdayToggle = {};
    
        while (classes.length < totalClasses) {
            const dayIndex = currentDate.getDay() === 0 ? 6 : currentDate.getDay() - 1;
            const isoDate = currentDate.toISOString().split("T")[0];
    
            if (!holidayDates.has(isoDate)) {
    
                const hasFirst = selectedWeekdays.includes(dayIndex);
                const hasSecond = selectedWeekdays2.includes(dayIndex);
    
                if ((hasFirst || hasSecond) && classes.length < totalClasses) {
    
                    if (hasFirst && hasSecond) {
                        const useFirst = weekdayToggle[dayIndex] ?? true;
    
                        const startTime = useFirst ? classStartTime : classStartTime2;
    
                        classes.push({
                            date: isoDate,
                            timeFrom: startTime,
                            timeTo: calculateEndTime(startTime),
                        });
    
                        weekdayToggle[dayIndex] = !useFirst;
                    } 
                    else if (hasFirst) {
                        classes.push({
                            date: isoDate,
                            timeFrom: classStartTime,
                            timeTo: calculateEndTime(classStartTime),
                        });
                    } 
                    else if (hasSecond) {
                        classes.push({
                            date: isoDate,
                            timeFrom: classStartTime2,
                            timeTo: calculateEndTime(classStartTime2),
                        });
                    }
                }
            }
    
            currentDate.setDate(currentDate.getDate() + 1);
        }
    
        return classes;
    };       

    const calculateEndTime = (startTime) => {
        if (!startTime || !Number(classEndTime)) return "";
    
        const [h, m] = startTime.split(":").map(Number);
        const date = new Date();
        date.setHours(h, m, 0, 0);
    
        const end = new Date(date.getTime() + Number(classEndTime) * 60000);
    
        return `${end.getHours().toString().padStart(2, "0")}:${end
            .getMinutes()
            .toString()
            .padStart(2, "0")}`;
    };
    
    const onArchiveGroup = () => {
        toast.warn(
            ({ closeToast }) => (
                <Toaster
                    closeToast={closeToast}
                    description={t("groups.confirmArchive")}
                    optionNo={t("no")}
                    optionYes={t("yes")}
                    onClick={() => {
                        closeToast();
                        navigate("/groups/" + groupId + "/archive-note");
                    }}
                />
            ),
            {
                autoClose: false,
                progress: false,
                closeButton: false,
            },
        );
    };

    const onStartGroup = () => {
        toast.warn(
            ({ closeToast }) => (
                <Toaster
                    closeToast={closeToast}
                    description={t("groups.confirmStart")}
                    optionNo={t("no")}
                    optionYes={t("yes")}
                    onClick={() => {
                        closeToast();
                        (async () => {
                            const selectedDates = await generateSelectedDates(classNumber);
                            
                            if (selectedDates.length === 0) {
                                toast.error("Morate odabrati dane i raspon datuma.");
                                return;
                            }
                        
                            const body = { dates: selectedDates };
                        
                            addGroupDates({ body, groupId }).then((result) => {
                                if (result?.error) {
                                    toast.error(result?.error?.data?.error?.responseMessage);
                                } else {
                                    toast.info(result?.data?.info?.responseMessage);
                                    groupStart({ groupId }).then((result) => {
                                        if (result?.error) {
                                            toast.error(result.error.data.error.responseMessage);
                                        } else {
                                            setStartGroup(false);
                                            setIsStarted(true);
                                            toast.info(result?.data?.info?.responseMessage);
                                        }
                                    });
                                }
                            });
                        })();
                    }}
                />
            ),
            {
                autoClose: false,
                progress: false,
                closeButton: false,
            },
        );
    };

    const gotoCalendarStep = (values) => {
        navigate("/groups/" + groupId + "/calendar/add");
    };

    const gotoStudentsStep = () => {
        navigate("/groups/" + groupId + "/students/add");
    };

    const gotoProfessorStep = () => {
        navigate("/groups/" + groupId + "/professor");
    };

    const yesNoOptions = () => {
        const options = [
            { id: "true", name: t("groups.isBlockClassYes") },
            { id: "false", name: t("groups.isBlockClassNo") },
        ];

        return prepareSelect(options);
    };

    const classDurations = () => {
        const options = [
            { id: 60, name: "60" },
            { id: 90, name: "90" },
        ];

        return prepareSelect(options);
    };

    useEffect(() => {
        locationList().then((result) => {
            setLocations(prepareSelect(result?.data?.records));
        });

        languageList().then((result) => {
            setLanguages(prepareSelect(result?.data?.records));
        });

        groupTypesList().then((result) => {
            setGroupTypes(prepareSelect(result?.data?.records));
        });
        setIsBlockClass(yesNoOptions);
        setClassDuration(classDurations);
        viewGroup(groupId).then((result) => {
            setIsArchived(result?.data?.isArchived);
            setGroupName(result?.data?.name);
            setHasTests(result?.data?.hasTests);
            setHasPlan(result?.data?.hasPlan);
            setHasReport(result?.data?.hasReport);
            setHasProgress(result?.data?.hasProgress);
            formRef.current.setValues(result?.data);
            setIsStarted(result?.data?.hasStarted);
            setSelectedWeekdays(Array.isArray(result.data.weekDays) ? result.data.weekDays : []);
            setSelectedWeekdays2(Array.isArray(result.data.weekDays2) ? result.data.weekDays2 : []);

            languageProgramList({ language: result?.data?.language }).then((result) => {
                setLanguageProgram(prepareSelect(result?.data?.records));
            });

            setClassNumber(result?.data?.classNumber);
            setClassStartTime(result?.data?.classStartTime);
            setClassStartTime2(result?.data?.classStartTime2);

            setClassEndTime(result?.data?.classDuration);

            if (result?.data?.hasStarted === true) {
                setStartGroup(false);
            }else{
                canGroupStart({groupId}).then((result) => {
                    setStartGroup(result?.data?.canStart);
                });
            }

            if (result?.data?.isArchived === true) {
                setCanArchiveGroup(false);
            }else{
                canGroupArchive({groupId}).then((result) => {
                    setCanArchiveGroup(result?.data?.canArchive);
                });
            }
        });

        appointmentList().then((result) => {
            let options = [];
            result?.data?.records.map((appointment) => {
                options.push({
                    id: appointment.id,
                    name: appointment.name,
                });
            });
            setAppointment(prepareSelect(options));
        });

        viewGroupProfessor({groupId}).then((result) => {
            if(result?.data){
                setStartDate(result?.data?.firstDate);
            }
        });
    }, []);

    return (
        <DefaultLayout>
            
            <TopNavigation>
                <ButtonBack to="/groups" />
                <Button title={t("save")} icon="fa-regular fa-floppy-disk" onClick={onSave}></Button>
                <Button title={t("groups.startGroup")} icon="fa-regular fa-play" onClick={onStartGroup} isDisabled={!startGroup}></Button>
                <Button title={t("groups.archiveGroup")} icon="fa-regular fa-box-archive" onClick={onArchiveGroup} isDisabled={!canArchiveGroup}></Button>
            </TopNavigation>
            <Tabs tabs={tabs} />
            <Main>
                <Title>{groupName}</Title>
                <Breadcrumps>
                    <Link title={t("groups.title")} icon="fa-light fa-users" />
                    <Link title={t("details")} isActive={true} />
                </Breadcrumps>
                <br />
                <br />
                <br />
                <Wizard>
                    <Step title={t("groups.basicInfo")} isActive={true} />
                    <Step title={t("groups.chooseStudents")} isChecked={true} onClick={gotoStudentsStep} />
                    <Step title={t("groups.chooseProfessor")} isChecked={true} onClick={gotoProfessorStep} />
                    <Step title={t("groups.selectDates")} isChecked={true} onClick={isStarted ? gotoCalendarStep : undefined} />
                </Wizard>
                <br />
                <br />
                <br />
                <Formik
                    innerRef={formRef}
                    initialValues={{
                        name: "",
                        location: null,
                        color: "",
                        professor: null,
                        coordinator: null,
                        note: "",
                        language: "",
                        program: "",
                        hasStarted: false,
                        isBlockClass: false,
                        type: null,
                        classStartTime: "",
                        classStartTime2: ""
                    }}
                    onSubmit={onSaved}
                >
                    {({ values, handleChange, setFieldValue }) => (
                        <Form>
                            <Row>
                                <Field>
                                    <Select
                                        name="language"
                                        label={t("groups.language")}
                                        onChange={(event) => {
                                            setFieldValue("language", event.target.value);
                                            languageProgramList({ language: event.target.value }).then((result) => {
                                                setLanguageProgram(prepareSelect(result?.data?.records));
                                            });
                                        }}
                                        required={true}
                                        options={languages}
                                        selectedValue={values.language}
                                        error={formErrors?.language}
                                        searchInDropdown={false}
                                    />
                                </Field>
                                <Field>
                                    <Select
                                        name="program"
                                        label={t("groups.languageProgram")}
                                        onChange={handleChange}
                                        required={true}
                                        options={languageProgram}
                                        selectedValue={values.program}
                                        error={formErrors?.program}
                                        searchInDropdown={false}
                                    />
                                </Field>
                            </Row>
                            <Row>
                                <Field>
                                    <Select
                                        name="location"
                                        label={t("groups.location")}
                                        onChange={handleChange}
                                        required={true}
                                        options={locations}
                                        selectedValue={values.location}
                                        error={formErrors?.location}
                                        searchInDropdown={false}
                                    />
                                </Field>
                                <Field>
                                    <Select
                                        name="type"
                                        label={t("groups.type")}
                                        onChange={handleChange}
                                        required={true}
                                        options={groupTypes}
                                        selectedValue={values.type}
                                        error={formErrors?.type}
                                        searchInDropdown={false}
                                    />
                                </Field>
                            </Row>
                            <Row>
                                <Field>
                                    <Input
                                        name="classNumber"
                                        label={t("groups.classNumber")}
                                        value={values.classNumber}
                                        onChange={handleChange}
                                        required={true}
                                        error={formErrors?.classNumber}
                                    />
                                </Field>
                                <Field>
                                    <Select
                                        name="classDuration"
                                        label={t("groups.classDuration")}
                                        onChange={handleChange}
                                        required={true}
                                        options={classDuration}
                                        selectedValue={values.classDuration}
                                        searchInDropdown={false}
                                    />
                                </Field>
                            </Row>
                            <Row>
                                <Field>
                                    <Select
                                        name="appointment"
                                        label={t("Termin kampa")}
                                        options={appointment}
                                        onChange={handleChange}
                                        selectedValue={values.appointment}
                                        error={formErrors?.appointment}
                                        searchInDropdown={false}
                                    />
                                </Field>
                                <Field></Field>
                            </Row>
                            <Divider title={t("Prvi termin")} />
                            <Row>
                                <Field>
                                    <TimePicker
                                        name="classStartTime"
                                        label={t("groups.classStartTime")}
                                        required={true}
                                        value={values.classStartTime}
                                        onChange={handleChange}
                                        error={formErrors?.classStartTime}
                                    />
                                </Field>
                                <Field></Field>
                            </Row>
                            <Row>
                                <Field>
                                    <div className="bulkCalendar__label">Kombinacija dana *</div>
                                    <div className="bulkCalendar__days">
                                        {weekDays.map((dayLabel, index) => (
                                            <div
                                                key={index}
                                                className={`bulkCalendar__day ${index >= 5 ? "weekend" : ""} ${selectedWeekdays?.includes(index) ? "active" : ""}`}
                                                onClick={() => toggleWeekday(index)}
                                            >
                                                {dayLabel}
                                            </div>
                                        ))}
                                    </div>
                                    <div className="bulkCalendar__error">{formErrors?.weekDays}</div>
                                </Field>
                            </Row>
                            <Divider title={t("Drugi termin")} />
                            <Row>
                                <Field>
                                    <TimePicker
                                        name="classStartTime2"
                                        label={t("groups.classStartTime2")}
                                        value={values.classStartTime2}
                                        onChange={handleChange}
                                        error={formErrors?.classStartTime2}
                                    />
                                </Field>
                                <Field></Field>
                            </Row>
                            <Row>
                                <Field>
                                    <div className="bulkCalendar__label">Kombinacija dana 2</div>
                                    <div className="bulkCalendar__days">
                                        {weekDays.map((dayLabel, index) => (
                                            <div
                                                key={index}
                                                className={`bulkCalendar__day ${index >= 5 ? "weekend" : ""} ${selectedWeekdays2.includes(index) ? "active" : ""}`}
                                                onClick={() => toggleWeekday2(index)}
                                            >
                                                {dayLabel}
                                            </div>
                                        ))}
                                    </div>
                                    <div className="bulkCalendar__error">{formErrors?.weekDays}</div>
                                </Field>
                            </Row>
                            <Divider title={t("additionalInformation")} />
                            <Row>
                                <Field isFullWidth={true}>
                                    <Textarea
                                        name="note"
                                        label={t("groups.note")}
                                        onChange={handleChange}
                                        value={values.note}
                                        rows={5}
                                        error={formErrors?.note}
                                    />
                                </Field>
                            </Row>
                            {isArchived && (
                            <Row>
                                <Field isFullWidth={true}>
                                    <Textarea
                                        label={t("groups.reasonForArchiving")}
                                        value={values.reasonForArchiving}
                                        rows={5}
                                        disabled={true}
                                    />
                                </Field>
                            </Row>
                            )}
                        </Form>
                    )}
                </Formik>
            </Main>
        </DefaultLayout>
    );
};

export default EditGroup;
